import { useTranslation } from "react-i18next";
import { Grid2, Typography } from "@mui/material";
import {
  toCurrency,
  toCurrencyWholeNumber,
  toRoundedNumberOfLots,
} from "@helpers";
import { GainLossPercents } from "@rentablo/components";
import { useMandatorContext } from "@rentablo/theming";
import TrendingUpIcon from "@icons/myicons/trending-up.svg?react";
import { InfoAccordion } from "../components";
import { AccordionSummaryHeader } from "./AccordionSummaryHeader";
import type { Investment } from "@rentablo/api/apiv1";
import type { GainLossValues } from "../helpers";

interface Props {
  investment: Investment;
  values: GainLossValues;
  currency: string;
}

export const ReturnOnInvestmentAccordion = ({
  investment,
  values,
  currency,
}: Props) => {
  const { t } = useTranslation();
  const { isKpmg } = useMandatorContext();
  const labelColor = isKpmg ? "textPrimary" : "textSecondary";

  return (
    <InfoAccordion
      id="return-on-investment"
      expandIconTestId="expand-return-on-investment"
      summary={
        <AccordionSummaryHeader
          icon={<TrendingUpIcon width={16} height={16} />}
          label={t("investment.details.gainLossStatistics.returnOnInvestment")}
          primaryValue={
            <Typography
              variant="body2"
              data-testid="gainLossStatistics.returnOnInvestment"
            >
              {toCurrency(values.returnOnInvestment, currency)}
            </Typography>
          }
          secondaryValue={
            <GainLossPercents value={values.returnOnInvestmentInPercent} />
          }
        />
      }
    >
      <Grid2 container rowSpacing={2} columnSpacing={2} mt={3}>
        <Grid2 size={{ xs: 7 }}>
          <Typography color={labelColor} variant="body3">
            {t("investment.details.gainLossStatistics.purchasePrice")}
          </Typography>
        </Grid2>
        <Grid2 size={{ xs: 5 }}>
          <Typography
            color="textPrimary"
            variant="body4"
            data-testid="gainLossStatistics.purchasePrice"
          >
            {toRoundedNumberOfLots(investment.numberOfLots)} x&nbsp;
            {toCurrencyWholeNumber(values.purchasePrice, currency)}
          </Typography>
        </Grid2>
        <Grid2 size={{ xs: 7 }}>
          <Typography color={labelColor} variant="body3">
            {t("investment.details.gainLossStatistics.investedAmount")}
          </Typography>
        </Grid2>
        <Grid2 size={{ xs: 5 }}>
          <Typography
            color="textPrimary"
            variant="body4"
            data-testid="gainLossStatistics.purchaseValue"
          >
            {toCurrencyWholeNumber(values.purchaseValue, currency)}
          </Typography>
        </Grid2>
        <Grid2 size={{ xs: 7 }}>
          <Typography color={labelColor} variant="body3">
            {t("investment.details.gainLossStatistics.marketValue")}
          </Typography>
        </Grid2>
        <Grid2 size={{ xs: 5 }}>
          <Typography
            color="textPrimary"
            variant="body4"
            data-testid="gainLossStatistics.currentMarketValue"
          >
            {toCurrencyWholeNumber(values.currentMarketValue, currency)}
          </Typography>
        </Grid2>
      </Grid2>
    </InfoAccordion>
  );
};
